/**
 * The quest engine — pure transition functions shared by the server (the
 * authority), the client (optimistic render) and the tests. No side effects
 * beyond mutating the passed `QuestInstance`; deterministic.
 */
import type { NpcDef } from "../npcs.js";
import type { NpcRef, QuestDef, QuestEvent, QuestInstance, SiteRef } from "./types.js";

/** True once every stage is cleared (ready to turn in). */
export function questReady(def: QuestDef, inst: QuestInstance): boolean {
  return inst.st >= def.stages.length;
}

/**
 * Apply an event to the CURRENT stage only: bump the matching objectives'
 * progress, and when every objective in the stage is met advance `st` and
 * reset `prog` for the next stage. Returns true iff anything changed.
 *
 * Matching rules (per the C3 contract):
 *  - kill:    enemy===undefined (any hostile) OR exact kind; adds ev.n.
 *  - collect: item matches; adds ev.n (clamped to the objective's n).
 *  - talk:    the event npc equals the RESOLVED target (inst.tg[stage][obj]).
 *  - deliver: never accrues from generic events — only an explicit deliver
 *             event (server-validated item consume) with a matching item and
 *             resolved npc target; adds ev.n.
 *  - goto:    the event town equals the resolved target town seed.
 */
export function applyQuestEvent(def: QuestDef, inst: QuestInstance, ev: QuestEvent): boolean {
  if (inst.st >= def.stages.length) return false; // already ready / complete
  const stage = def.stages[inst.st];
  if (!stage) return false;
  const tgStage = inst.tg[inst.st] ?? [];
  let changed = false;

  for (let oi = 0; oi < stage.objectives.length; oi++) {
    const obj = stage.objectives[oi];
    if (obj.kind !== ev.k) continue;
    const have = inst.prog[oi] ?? 0;
    if (have >= obj.n) continue; // objective already met
    const target = tgStage[oi] ?? null;
    let add = 0;

    switch (obj.kind) {
      case "kill":
        if (obj.enemy === undefined || obj.enemy === ev.enemy) add = ev.n ?? 1;
        break;
      case "collect":
        if (obj.item === ev.item) add = ev.n ?? 1;
        break;
      case "talk":
        // Talk credits only the resolved target persona.
        if (ev.npc !== undefined && (target === null ? ev.npc === refNpc(obj.npc) : ev.npc === target)) add = 1;
        break;
      case "deliver": {
        const itemOk = obj.item === undefined || obj.item === ev.item;
        const npcOk = target === null ? ev.npc === undefined || ev.npc === refNpc(obj.npc) : ev.npc === target;
        if (itemOk && npcOk) add = ev.n ?? 1;
        break;
      }
      case "goto":
        if (ev.town !== undefined && (target === null || ev.town === target)) add = 1;
        break;
    }

    if (add <= 0) continue;
    const next = Math.min(obj.n, have + add);
    if (next !== have) {
      inst.prog[oi] = next;
      changed = true;
    }
  }

  if (!changed) return false;

  // Stage complete → advance and reset progress for the next stage.
  if (stage.objectives.every((obj, oi) => (inst.prog[oi] ?? 0) >= obj.n)) {
    inst.st += 1;
    const nextStage = def.stages[inst.st];
    inst.prog = new Array(nextStage ? nextStage.objectives.length : 0).fill(0);
  }
  return true;
}

/**
 * Resolve every role-relative NpcRef / SiteRef to concrete ids for ALL
 * stages, once, at accept. Deterministic: role-relative npcs scan the
 * (stable) roster preferring an exact role match, then a keeper, then the
 * first persona — so two stages that reference the same role resolve to the
 * SAME persona (the campaign relies on this).
 */
export function resolveTargets(
  def: QuestDef,
  settlementId: string,
  roster: readonly NpcDef[],
  canonicalNpc?: (slug: NonNullable<NpcRef["canon"]>) => string | null,
): (string | null)[][] {
  return def.stages.map((stage) =>
    stage.objectives.map((obj) => {
      if (obj.kind === "talk" || obj.kind === "deliver") return resolveNpcRef(obj.npc, roster, canonicalNpc);
      if (obj.kind === "goto") return resolveSiteRef(obj.site, settlementId);
      return null;
    }),
  );
}

/** The concrete id of a purely-concrete NpcRef (else null). */
function refNpc(ref: NpcRef | undefined): string | null {
  return ref?.npc ?? null;
}

function resolveNpcRef(
  ref: NpcRef | undefined,
  roster: readonly NpcDef[],
  canonicalNpc?: (slug: NonNullable<NpcRef["canon"]>) => string | null,
): string | null {
  if (!ref) return roster[0]?.id ?? null;
  if (ref.npc) return ref.npc; // concrete
  if (ref.canon) return canonicalNpc?.(ref.canon) ?? null;
  if (ref.role) {
    const exact = roster.find((n) => n.role === ref.role);
    if (exact) return exact.id;
    const keeper = roster.find((n) => n.role === "keeper");
    if (keeper) return keeper.id;
    return roster[0]?.id ?? null;
  }
  return roster[0]?.id ?? null;
}

function resolveSiteRef(ref: SiteRef | undefined, settlementId: string): string | null {
  if (!ref) return settlementId;
  if (ref.town) return ref.town;
  return settlementId; // here (default)
}

/** A fresh instance with targets pre-resolved for the accepting settlement. */
export function makeInstance(
  def: QuestDef, settlementId: string, roster: readonly NpcDef[], participants: string[], epoch: number,
  canonicalNpc?: (slug: NonNullable<NpcRef["canon"]>) => string | null,
): QuestInstance {
  const tg = resolveTargets(def, settlementId, roster, canonicalNpc);
  const first = def.stages[0];
  return {
    id: def.id,
    q: def.id,
    ps: participants,
    st: 0,
    prog: new Array(first ? first.objectives.length : 0).fill(0),
    tg,
    ep: epoch,
  };
}
